import MUIDialog from '@material-ui/core/Dialog';
import MUIDialogActions from '@material-ui/core/DialogActions';
import MUIDialogContent from '@material-ui/core/DialogContent';
import MUIDialogTitle from '@material-ui/core/DialogTitle';
import MUIDivider from '@material-ui/core/Divider';
import { Editor } from '@tiptap/core';
import { Button, Heading, Input } from 'copilot-design-system';
import { Formik } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { URLValidationRegex } from 'src/constants';
import { UrlUtils } from 'src/utils';

interface AddLinkModalProps {
  editor: Editor;
  open: boolean;
  onClose: () => void;
  /** For inserting via slash command - the range to replace */
  insertRange?: { from: number; to: number };
}

const linkSchema = z.object({
  displayText: z.string().optional(),
  linkUrl: z
    .string()
    .regex(URLValidationRegex, 'Please enter a valid URL')
    .or(z.literal('')),
});

type LinkFormValues = z.infer<typeof linkSchema>;

/**
 * A reusable modal for adding/editing links in TipTap editors.
 *
 * Behavior:
 * - If editor has text selected → shows only URL field, sets link on selection
 * - If no selection (or insertRange provided) → shows both Display Text and URL fields, inserts new link
 */
export const AddLinkModal = ({
  editor,
  open,
  onClose,
  insertRange,
}: AddLinkModalProps) => {
  const { selection } = editor.state;
  const hasSelection = !selection.empty;
  const existingUrl: string = editor.getAttributes('link').href ?? '';

  // Determine mode: if we have selected text, we only need the URL
  // Otherwise, we need both display text and URL to insert a new link
  const showDisplayTextField = !hasSelection || insertRange !== undefined;

  const initialValues: LinkFormValues = {
    displayText: '',
    linkUrl: existingUrl,
  };

  const handleSubmit = (values: LinkFormValues) => {
    const { displayText, linkUrl } = values;

    if (linkUrl === '') {
      // Remove link
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (showDisplayTextField && displayText) {
      // Insert new link with display text
      const range = insertRange ?? { from: selection.from, to: selection.to };
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent(
          `<a href='${UrlUtils.GetFullUrl(linkUrl)}'>${displayText}</a> `,
        )
        .createParagraphNear()
        .run();
    } else {
      // Set link on existing selection
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: UrlUtils.GetFullUrl(linkUrl) })
        .run();
    }

    onClose();
  };

  return (
    <MUIDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      transitionDuration={0}
    >
      <MUIDialogTitle className="!pb-0">
        <Heading size="lg">Add a link</Heading>
      </MUIDialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={toFormikValidationSchema(linkSchema)}
        validateOnBlur
        validateOnChange
      >
        {({
          errors,
          handleBlur,
          handleChange,
          submitForm,
          touched,
          values,
        }) => (
          <>
            <MUIDialogContent className="!py-4">
              <div className="flex flex-col gap-4">
                {showDisplayTextField && (
                  <Input
                    autoFocus
                    placeholder="Display text"
                    label="Name"
                    name="displayText"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.displayText ?? ''}
                    error={
                      touched.displayText && errors.displayText !== undefined
                        ? errors.displayText
                        : undefined
                    }
                  />
                )}
                <Input
                  autoFocus={!showDisplayTextField}
                  placeholder="https://example.com"
                  label="Link"
                  name="linkUrl"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.linkUrl}
                  error={
                    touched.linkUrl && errors.linkUrl !== undefined
                      ? errors.linkUrl
                      : undefined
                  }
                />
              </div>
            </MUIDialogContent>
            <MUIDivider />
            <MUIDialogActions className="!px-4 !py-3">
              <Button
                label="Cancel"
                onClick={onClose}
                variant="secondary"
                className="px-4 py-1"
              />
              <Button
                label={existingUrl ? 'Save' : 'Add'}
                onClick={submitForm}
                className="px-4 py-1 ml-2"
              />
            </MUIDialogActions>
          </>
        )}
      </Formik>
    </MUIDialog>
  );
};
